import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormControl,
  InputAdornment,
  MenuItem,
  Typography,
} from "@mui/material";
import { AutonomousActivitySwitcher } from "portal/components/atk/AutonomousActivitySwitcher";
import { AutonomousActivityType } from "protos/atk/enums";
import { CheckboxWithLabel, Select, TextField } from "formik-mui";
import { Field, useFormikContext } from "formik";
import { FormikDimensionedNumberInput } from "common/components/FormikDimensionedNumberInput";
import {
  friendlyScvOutputName,
  resolveTractorConfiguration,
} from "portal/utils/atkEquipment";
import { RigDetailsFormState } from "../FormState";
import { typedPath } from "typed-path";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { useMemo, useState } from "react";

const S = typedPath<RigDetailsFormState>();

export const ThreePointAutonomySubform: React.FC<{ readOnly: boolean }> = ({
  readOnly,
}) => {
  const { t } = useTranslation();
  const { measurementSystem } = useSelf();
  const { values } = useFormikContext<RigDetailsFormState>();
  const [viewingActivity, setViewingActivity] =
    useState<AutonomousActivityType>(
      AutonomousActivityType.ACTIVITY_WORKING_ROW
    );

  const resolvedTractor = useMemo(
    () =>
      values.tractor
        ? resolveTractorConfiguration(t, values.tractor)
        : undefined,
    [t, values.tractor]
  );

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        {t(
          "views.autotractor.rigDetails.configuration.threePointAutonomy.sectionHeader"
        )}
      </AccordionSummary>
      <AccordionDetails className="grid grid-cols-2 gap-4 items-end">
        <FormControl fullWidth className="col-span-full">
          <Field
            name={S.atkConfig.threePointAutonomy.enable.$path}
            component={CheckboxWithLabel}
            type="checkbox"
            Label={{
              label: t(
                "views.autotractor.rigDetails.configuration.threePointAutonomy.enable"
              ),
            }}
            disabled={readOnly}
          />
        </FormControl>
        {values.atkConfig.threePointAutonomy.enable && (
          <>
            <Typography>
              {t(
                "views.autotractor.autonomousActivities.activitySelectionLabel"
              )}
            </Typography>
            <AutonomousActivitySwitcher
              activeActivity={viewingActivity}
              setActiveActivity={setViewingActivity}
            />
            <FormControl fullWidth>
              <Typography variant="body1" id="entry-label">
                {t(
                  "views.autotractor.autonomousActivities.momentaryControlMode.header"
                )}
              </Typography>
              <Field
                component={Select}
                name={
                  S.atkConfig.activities.activities[viewingActivity]
                    .rearThreePtControl.entryActivity.$path
                }
                aria-labelledby="entry-label"
                disabled={readOnly}
              >
                <MenuItem value="go-to-height">
                  {t(
                    "views.autotractor.autonomousActivities.momentaryControlMode.goToPosition"
                  )}
                </MenuItem>
                <MenuItem value="detent-up">
                  {t(
                    "views.autotractor.autonomousActivities.auxControlAction.detentUp"
                  )}
                </MenuItem>
                <MenuItem value="detent-down">
                  {t(
                    "views.autotractor.autonomousActivities.auxControlAction.detentDown"
                  )}
                </MenuItem>
                <MenuItem value="none">
                  {t(
                    "views.autotractor.autonomousActivities.auxControlAction.none"
                  )}
                </MenuItem>
              </Field>
            </FormControl>
            <FormControl fullWidth>
              <Typography id="entry-target-height-label">
                {t(
                  "views.autotractor.autonomousActivities.heightControlTargetPositionLabel"
                )}
              </Typography>
              <Field
                component={TextField}
                name={
                  S.atkConfig.activities.activities[viewingActivity]
                    .rearThreePtControl.entryGoToHeightPct.$path
                }
                disabled={
                  values.atkConfig.activities.activities[viewingActivity]
                    ?.rearThreePtControl.entryActivity !== "go-to-height" ||
                  readOnly
                }
                aria-labelledby="entry-target-height-label"
                className="mt-2"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        {t("utils.units.%")}
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </FormControl>
            <FormControl fullWidth>
              <Typography variant="body1" id="hold-label">
                {t(
                  "views.autotractor.autonomousActivities.holdControlMode.header"
                )}
              </Typography>
              <Field
                component={Select}
                name={
                  S.atkConfig.activities.activities[viewingActivity]
                    .rearThreePtControl.holdActivity.$path
                }
                aria-labelledby="hold-label"
                disabled={readOnly}
              >
                <MenuItem value="hold-height">
                  {t(
                    "views.autotractor.autonomousActivities.heightControlMode.holdHeight"
                  )}
                </MenuItem>
                <MenuItem value="hold-float">
                  {t(
                    "views.autotractor.autonomousActivities.auxControlAction.float"
                  )}
                </MenuItem>
                <MenuItem value="none">
                  {t(
                    "views.autotractor.autonomousActivities.auxControlAction.none"
                  )}
                </MenuItem>
              </Field>
            </FormControl>
            <FormControl fullWidth>
              <Typography id="hold-target-height-label">
                {t(
                  "views.autotractor.autonomousActivities.heightControlTargetPositionLabel"
                )}
              </Typography>
              <Field
                component={TextField}
                name={
                  S.atkConfig.activities.activities[viewingActivity]
                    .rearThreePtControl.holdHeightPct.$path
                }
                disabled={
                  values.atkConfig.activities.activities[viewingActivity]
                    ?.rearThreePtControl.holdActivity !== "hold-height" ||
                  readOnly
                }
                aria-labelledby="hold-target-height-label"
                className="mt-2"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        {t("utils.units.%")}
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </FormControl>
            <hr className="w-full col-span-full" />
            <FormControl fullWidth>
              <FormikDimensionedNumberInput
                label={t(
                  "views.autotractor.rigDetails.configuration.threePointAutonomy.liftTimeout"
                )}
                name={S.atkConfig.threePointAutonomy.maxLiftTimeMs.$path}
                validate={true}
                tooltipText={t(
                  "views.autotractor.rigDetails.configuration.threePointAutonomy.liftTimeoutTooltip"
                )}
                dimensionedNumberInputProps={{
                  storedUnit: "ms",
                  displayUnits: {
                    metric: "s",
                    imperial: "s",
                  },
                  localSystem: measurementSystem,
                  disabled: readOnly,
                }}
                className="w-full self-end"
              />
            </FormControl>
            <FormControl fullWidth>
              <FormikDimensionedNumberInput
                label={t(
                  "views.autotractor.rigDetails.configuration.threePointAutonomy.lowerTimeout"
                )}
                name={S.atkConfig.threePointAutonomy.maxLowerTimeMs.$path}
                validate={true}
                tooltipText={t(
                  "views.autotractor.rigDetails.configuration.threePointAutonomy.lowerTimeoutTooltip"
                )}
                dimensionedNumberInputProps={{
                  storedUnit: "ms",
                  displayUnits: {
                    metric: "s",
                    imperial: "s",
                  },
                  localSystem: measurementSystem,
                  disabled: readOnly,
                }}
                className="w-full self-end"
              />
            </FormControl>
            <FormControl fullWidth className="col-span-full">
              <Typography>
                {t(
                  "views.autotractor.rigDetails.configuration.threePointAutonomy.toplinkScv"
                )}
              </Typography>
              <Field
                name={
                  S.atkConfig.threePointAutonomy.controls.toplinkScvOutputId
                    .$path
                }
                component={Select}
                displayEmpty
                disabled={readOnly}
              >
                <MenuItem value="">{t("utils.descriptors.none")}</MenuItem>
                {(resolvedTractor?.scvOutputs?.scvOutput ?? []).map(
                  (scvOutput) => (
                    <MenuItem key={scvOutput.id} value={scvOutput.id}>
                      {friendlyScvOutputName(t, scvOutput)}
                    </MenuItem>
                  )
                )}
              </Field>
            </FormControl>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
